import { ChangeDetectionStrategy, Component, input, output } from "@angular/core";
import { CommonModule } from "@angular/common";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { ItemTileComponent, TileItem } from "./item.tile.component";
import { S25IconComponent } from "../../s25-icon/s25.icon.component";
import { SESearchType } from "./se.search.component";
import { S25States } from "../../../services/state/state.map";
import { S25LoadMoreComponent } from "../../../standalone/s25.load.more.component";

@TypeManagerDecorator("s25-ng-se-search-results")
@Component({
    selector: "s25-ng-se-search-results",
    template: `
        @if (hasSearched()) {
            <div class="results-section">
                @if (!results().length && isSearching()) {
                    <div class="loading-state">
                        <s25-ng-icon [type]="'refresh'" [size]="24"></s25-ng-icon>
                        <p>Searching...</p>
                    </div>
                } @else if (results().length > 0) {
                    <div class="results-header">
                        <h3>
                            {{ resultsCount() }} {{ searchType() === "events" ? "Event" : "Organization"
                            }}{{ resultsCount() !== 1 ? "s" : "" }} Found
                        </h3>
                    </div>
                    <div
                        class="results-grid"
                        [class.events-grid]="searchType() === 'events'"
                        [class.orgs-grid]="searchType() === 'organizations'"
                    >
                        @for (item of results(); track item.itemId) {
                            <se-item-tile [item]="item" [routingStateName]="getRoutingState()"></se-item-tile>
                        }
                    </div>

                    <s25-ng-load-more
                        [currentCount]="results().length"
                        [totalCount]="resultsCount()"
                        [isLoading]="isSearching()"
                        (loadMore)="onLoadMore()"
                    ></s25-ng-load-more>
                } @else {
                    <div class="no-results">
                        <s25-ng-icon [type]="'search'" [size]="48"></s25-ng-icon>
                        <h3>No results found</h3>
                        <p>Try adjusting your search terms or filters</p>
                    </div>
                }
            </div>
        }
    `,
    styles: `
        .results-section {
            position: relative;
            max-width: 1400px;
            margin: 0 auto;
            padding: 2rem 1rem;
            background: #f8f9fa;
            border-radius: 8px;
        }

        .loading-state {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            padding: 3rem 1rem;
            color: #666;
        }

        .loading-state p {
            margin-top: 1rem;
            font-size: 1rem;
        }

        ::ng-deep .loading-state svg {
            animation: spin 1s linear infinite;
        }

        @keyframes spin {
            from {
                transform: rotate(0deg);
            }
            to {
                transform: rotate(360deg);
            }
        }

        .results-header {
            margin-bottom: 1.5rem;
        }

        .results-header h3 {
            font-size: 1.25rem;
            font-weight: 600;
            color: #333;
            margin: 0;
        }

        .results-grid {
            display: grid;
            gap: 1.5rem;
        }

        .results-grid.events-grid {
            grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
        }

        .results-grid.orgs-grid {
            grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
        }

        .no-results {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            padding: 3rem 1rem;
            text-align: center;
            color: #666;
        }

        .no-results h3 {
            margin: 1rem 0 0.5rem 0;
            font-size: 1.25rem;
            color: #333;
        }

        .no-results p {
            margin: 0;
            font-size: 1rem;
        }

        ::ng-deep .no-results svg {
            opacity: 0.4;
        }

        @media (max-width: 768px) {
            .results-grid.events-grid,
            .results-grid.orgs-grid {
                grid-template-columns: 1fr;
            }
        }
    `,
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, ItemTileComponent, S25IconComponent, S25LoadMoreComponent],
})
export class SeSearchResultsComponent {
    // Input signals
    results = input.required<TileItem[]>();
    resultsCount = input.required<number>();
    hasSearched = input.required<boolean>();
    isSearching = input.required<boolean>();
    searchType = input.required<SESearchType>();
    currentPage = input.required<number>();
    pageSize = input.required<number>();

    // Output events
    pageChange = output<number>();
    loadMore = output<void>();

    /**
     * Get the routing state name based on search type
     */
    getRoutingState(): S25States.Name {
        return this.searchType() === "events"
            ? S25States.names.studentEngagementEvent
            : S25States.names.studentEngagementOrganization;
    }

    /**
     * Handle page change from pagination component
     */
    onPageChange(newPage: number): void {
        this.pageChange.emit(newPage);
    }

    /**
     * Handle load more from load more component
     */
    onLoadMore(): void {
        this.loadMore.emit();
    }
}
